import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import Select, { StylesConfig } from 'react-select';
import { UseFormMethods } from 'react-hook-form';
import useFetch from 'use-http';
import { useInfiniteScroll, IPagedResult } from 'src/util/useInfiniteScroll';

interface ICountryOption {
    value: string; // country abbreviation (e.g. "US")
    label: string; // country name
}

interface ICountrySelectProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'setValue'>> {
    name: string;
    defaultCountry?: string;
    isDisabled?: boolean;
    style?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    onCountryChange?: (country: string) => void;
}

// Debounced search-as-you-type Country picker, backed by DLCTSDI.dbo.COUNTRY_CODES (the same
// table Oracle's own COUNTRY field validates against) via /v1.0/ClientProjectMaintenance/countries.
// Paging reuses the shared useInfiniteScroll hook - with no search term the endpoint's first page
// returns COUNTRY_CODES' first 25 rows by ID (US/Canada lead, matching its own seeded order);
// scrolling the open menu to the bottom pages in the next 25, same as any other paged grid in the
// app. Typing resets to page 1 of the filtered (name/abbreviation) results.
// Modeled directly on GLAccountSelect.tsx's search-as-you-type pattern.
const CountrySelect = (props: ICountrySelectProps) => {
    const { get, response } = useFetch();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCountry, setSelectedCountry] = useState<string | undefined>(props.defaultCountry);
    const [defaultOption, setDefaultOption] = useState<ICountryOption | undefined>(
        props.defaultCountry ? { value: props.defaultCountry, label: props.defaultCountry } : undefined
    );

    const fetchPage = async (pageNumber: number): Promise<IPagedResult<ICountryOption>> => {
        const result = await get(
            `/v1.0/ClientProjectMaintenance/countries?search=${encodeURIComponent(searchTerm)}&pageNumber=${pageNumber}`
        );

        if (!response.ok || !result) {
            return { items: [], totalRecords: 0 };
        }

        return {
            items: (result.items ?? []).map((c: { code: string; description: string }) => ({
                value: c.code,
                label: c.description,
            })),
            totalRecords: result.totalRecords ?? 0,
        };
    };

    const { items: options, hasMore, loadNextPage, reset } = useInfiniteScroll<ICountryOption>(fetchPage, 25);

    useEffect(() => {
        setSelectedCountry(props.defaultCountry);
        if (props.defaultCountry) {
            setDefaultOption({ value: props.defaultCountry, label: props.defaultCountry });
        }
    }, [props.defaultCountry]);

    // Fires on mount (searchTerm starts at '', loading page 1 of the unfiltered list) and again on
    // every new search term - reset() clears the accumulated pages first so loadNextPage() doesn't
    // just append the filtered results onto stale ones from the previous term.
    useEffect(() => {
        reset();
        loadNextPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const debouncedSearch = useRef(_.debounce((term: string) => setSearchTerm(term), 800)).current;

    const handleInputChange = (value: string, meta: { action: string }) => {
        if (meta.action === 'input-change') {
            debouncedSearch(value);
        }
        return value;
    };

    const handleChange = (option: ICountryOption | null) => {
        setSelectedCountry(option?.value);
        props.setValue?.(props.name, option?.value ?? '', { shouldDirty: true });
        props.onCountryChange?.(option?.value ?? '');
    };

    const selectedOption = options.find((o) => o.value === selectedCountry)
        ?? (selectedCountry === defaultOption?.value ? defaultOption : null)
        ?? (selectedCountry ? { value: selectedCountry, label: selectedCountry } : null);

    // Enlarged from the 9px GLAccountSelect baseline per explicit request for this field.
    const customStyles: StylesConfig<ICountryOption, false> = {
        control: (provided) => ({
            ...provided,
            minHeight: '36px',
            height: '36px',
            fontSize: '14px',
            border: 'none',
            borderRadius: 0,
            borderBottom: '1px solid grey',
            boxShadow: 'none',
            backgroundColor: 'white',
        }),
        valueContainer: (provided) => ({ ...provided, padding: '0 4px', height: '36px' }),
        input: (provided) => ({ ...provided, margin: 0, padding: 0, fontSize: '14px' }),
        singleValue: (provided) => ({ ...provided, fontSize: '14px' }),
        placeholder: (provided) => ({ ...provided, fontSize: '14px' }),
        option: (provided) => ({ ...provided, fontSize: '14px' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '36px' }),
        dropdownIndicator: (provided) => ({ ...provided, padding: '0 2px' }),
        clearIndicator: (provided) => ({ ...provided, padding: '0 2px' }),
    };

    const formatOptionLabel = (option: ICountryOption) => option.label;

    const handleMenuScrollToBottom = () => {
        if (hasMore) {
            loadNextPage();
        }
    };

    return (
        <div style={props.bodyStyle ?? { width: '100%' }}>
            <input type="hidden" name={props.name} ref={props.register} defaultValue={props.defaultCountry} />
            <Select
                isClearable={true}
                isSearchable={true}
                isDisabled={props.isDisabled}
                options={options}
                value={selectedOption}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onMenuScrollToBottom={handleMenuScrollToBottom}
                formatOptionLabel={formatOptionLabel}
                placeholder="Search Countries..."
                styles={customStyles}
            />
        </div>
    );
};

export default CountrySelect;



// WEBPACK FOOTER //
// ./src/components/client-project/components/CountrySelect.tsx